import React, { useState } from 'react';
import { ChevronRightIcon, BellIcon, MessageSquareIcon, BookmarkIcon, UserIcon, FileTextIcon } from 'lucide-react';
import { NotificationChannels } from './NotificationChannels';
import { NotificationRules } from './NotificationRules';
import { UserPreferences } from './UserPreferences';
import { DocumentOverrides } from './DocumentOverrides';
// Types
export type NotificationChannel = {
  id: string;
  type: 'teams' | 'slack' | 'email' | 'webhook';
  name: string;
  isEnabled: boolean;
  isConfigured: boolean;
  configuration: TeamsConfig | SlackConfig | EmailConfig | WebhookConfig;
  createdAt: string;
  updatedAt: string;
};
export type TeamsConfig = {
  tenantId: string;
  appRegistrationId: string;
  clientSecret: string;
  deliveryMethod: 'direct' | 'channel';
  defaultChannel?: string;
};
export type SlackConfig = {
  workspaceId: string;
  botToken: string;
  defaultChannel: string;
};
export type EmailConfig = {
  fromAddress: string;
  connectionType: 'smtp' | 'microsoft365' | 'gmail';
  smtpHost?: string;
  smtpPort?: number;
  username?: string;
  password?: string;
  useTLS?: boolean;
};
export type WebhookConfig = {
  url: string;
  authType: 'none' | 'basic' | 'bearer';
  username?: string;
  password?: string;
  token?: string;
  headers?: Record<string, string>;
};
export type ScopeLevel = 'Tenant' | 'Entity' | 'Site';
export type NotificationEvent = {
  id: string;
  name: string;
  description: string;
  category: 'system' | 'workflow' | 'document' | 'user' | 'vendor';
  defaultEnabled: boolean;
};
export type NotificationRule = {
  id: string;
  eventId: string;
  name: string;
  description: string;
  channels: string[]; // Array of channel IDs
  frequency: 'immediate' | 'daily' | 'weekly';
  isEnabled: boolean;
  filters: {
    entityIds?: string[];
    siteIds?: string[];
    documentTypes?: string[];
    userRoles?: string[];
  };
  scopeLevel: ScopeLevel;
  entityId?: string;
  siteId?: string;
  createdAt: string;
  updatedAt: string;
};
export type UserNotificationPreference = {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  receiveMentions: boolean;
  receiveApprovalReminders: boolean;
  receiveSystemAlerts: boolean;
  preferredChannel: 'teams' | 'slack' | 'email' | 'in-app';
  mutedEvents: string[]; // Array of event IDs
  createdAt: string;
  updatedAt: string;
};
export type DocumentNotificationOverride = {
  id: string;
  documentId: string;
  documentType: 'invoice' | 'po' | 'requisition' | 'vendor' | 'receipt';
  documentNumber: string;
  documentName: string;
  overrideType: 'mute' | 'force';
  affectedEvents: string[]; // Array of event IDs or 'all'
  affectedUsers: string[]; // Array of user IDs or 'all'
  expiresAt?: string;
  createdBy: string;
  createdAt: string;
};
export function NotificationsManagement({
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState<'channels' | 'rules' | 'preferences' | 'overrides'>('channels');
  // Mock data for notification channels
  const [channels, setChannels] = useState<NotificationChannel[]>([{
    id: 'channel-001',
    type: 'teams',
    name: 'Microsoft Teams',
    isEnabled: true,
    isConfigured: true,
    configuration: {
      tenantId: 'tenant-123',
      appRegistrationId: 'app-456',
      clientSecret: '********',
      deliveryMethod: 'channel',
      defaultChannel: 'General'
    },
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-02-20T14:30:00Z'
  }, {
    id: 'channel-002',
    type: 'slack',
    name: 'Slack',
    isEnabled: false,
    isConfigured: false,
    configuration: {
      workspaceId: '',
      botToken: '',
      defaultChannel: ''
    },
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-01-15T10:00:00Z'
  }, {
    id: 'channel-003',
    type: 'email',
    name: 'Email Notifications',
    isEnabled: true,
    isConfigured: true,
    configuration: {
      fromAddress: 'notifications@rapidp2p.com',
      connectionType: 'microsoft365',
      username: 'notifications@rapidp2p.com',
      password: '********'
    },
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-03-10T09:15:00Z'
  }, {
    id: 'channel-004',
    type: 'webhook',
    name: 'Custom Webhook',
    isEnabled: false,
    isConfigured: false,
    configuration: {
      url: '',
      authType: 'none'
    },
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-01-15T10:00:00Z'
  }]);
  // Mock data for notification events
  const [events, setEvents] = useState<NotificationEvent[]>([{
    id: 'event-001',
    name: 'Mention in Note',
    description: 'You were mentioned in a note on a document',
    category: 'user',
    defaultEnabled: true
  }, {
    id: 'event-002',
    name: 'Approval Required',
    description: 'A document requires your approval',
    category: 'workflow',
    defaultEnabled: true
  }, {
    id: 'event-003',
    name: 'Document Processing Failed',
    description: 'A document failed during automated processing',
    category: 'document',
    defaultEnabled: true
  }, {
    id: 'event-004',
    name: 'Vendor Onboarding Action',
    description: 'Action needed for vendor onboarding',
    category: 'vendor',
    defaultEnabled: true
  }, {
    id: 'event-005',
    name: 'System Maintenance',
    description: 'System maintenance scheduled',
    category: 'system',
    defaultEnabled: true
  }]);
  // Mock data for notification rules
  const [rules, setRules] = useState<NotificationRule[]>([{
    id: 'rule-001',
    eventId: 'event-001',
    name: 'Mention Notifications',
    description: 'Send notification when user is mentioned',
    channels: ['channel-001', 'channel-003'],
    frequency: 'immediate',
    isEnabled: true,
    filters: {},
    scopeLevel: 'Tenant',
    createdAt: '2023-02-01T10:00:00Z',
    updatedAt: '2023-02-01T10:00:00Z'
  }, {
    id: 'rule-002',
    eventId: 'event-002',
    name: 'Approval Notifications',
    description: 'Send notification when approval is required',
    channels: ['channel-001', 'channel-003'],
    frequency: 'immediate',
    isEnabled: true,
    filters: {},
    scopeLevel: 'Tenant',
    createdAt: '2023-02-01T10:00:00Z',
    updatedAt: '2023-02-01T10:00:00Z'
  }, {
    id: 'rule-003',
    eventId: 'event-003',
    name: 'Processing Error Notifications',
    description: 'Send notification when document processing fails',
    channels: ['channel-001', 'channel-003'],
    frequency: 'daily',
    isEnabled: true,
    filters: {},
    scopeLevel: 'Tenant',
    createdAt: '2023-02-01T10:00:00Z',
    updatedAt: '2023-02-01T10:00:00Z'
  }]);
  // Mock data for user preferences
  const [userPreferences, setUserPreferences] = useState<UserNotificationPreference[]>([{
    id: 'pref-001',
    userId: 'user-001',
    userName: 'John Smith',
    userEmail: 'john.smith@example.com',
    receiveMentions: true,
    receiveApprovalReminders: true,
    receiveSystemAlerts: false,
    preferredChannel: 'teams',
    mutedEvents: [],
    createdAt: '2023-02-15T10:00:00Z',
    updatedAt: '2023-02-15T10:00:00Z'
  }, {
    id: 'pref-002',
    userId: 'user-002',
    userName: 'Jane Doe',
    userEmail: 'jane.doe@example.com',
    receiveMentions: true,
    receiveApprovalReminders: true,
    receiveSystemAlerts: true,
    preferredChannel: 'email',
    mutedEvents: ['event-005'],
    createdAt: '2023-02-15T10:00:00Z',
    updatedAt: '2023-02-15T10:00:00Z'
  }]);
  // Mock data for document overrides
  const [documentOverrides, setDocumentOverrides] = useState<DocumentNotificationOverride[]>([{
    id: 'override-001',
    documentId: 'doc-001',
    documentType: 'invoice',
    documentNumber: 'INV-2023-001',
    documentName: 'Office Supplies Invoice',
    overrideType: 'mute',
    affectedEvents: ['all'],
    affectedUsers: ['all'],
    expiresAt: '2023-12-31T23:59:59Z',
    createdBy: 'John Smith',
    createdAt: '2023-03-01T10:00:00Z'
  }, {
    id: 'override-002',
    documentId: 'doc-002',
    documentType: 'po',
    documentNumber: 'PO-2023-042',
    documentName: 'IT Equipment Purchase Order',
    overrideType: 'force',
    affectedEvents: ['event-002'],
    affectedUsers: ['user-001', 'user-002'],
    createdBy: 'Jane Doe',
    createdAt: '2023-03-05T14:30:00Z'
  }]);
  // Handle tab switching
  const renderTabContent = () => {
    switch (activeTab) {
      case 'channels':
        return <NotificationChannels channels={channels} setChannels={setChannels} locale={locale} data-id="element-17399" />;
      case 'rules':
        return <NotificationRules rules={rules} setRules={setRules} events={events} channels={channels} locale={locale} data-id="element-17400" />;
      case 'preferences':
        return <UserPreferences userPreferences={userPreferences} setUserPreferences={setUserPreferences} events={events} locale={locale} data-id="element-17401" />;
      case 'overrides':
        return <DocumentOverrides documentOverrides={documentOverrides} setDocumentOverrides={setDocumentOverrides} events={events} locale={locale} data-id="element-17402" />;
      default:
        return <NotificationChannels channels={channels} setChannels={setChannels} locale={locale} data-id="element-17403" />;
    }
  };
  return <div data-id="element-17404">
      <div className="mb-8" data-id="element-17405">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-17406">Notifications</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17407">
          Manage notification channels, rules, and preferences for RapidP2P
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb" data-id="element-17408">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-17409">
          <li className="inline-flex items-center" data-id="element-17410">
            <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-17411">
              <BookmarkIcon className="w-4 h-4 mr-2" data-id="element-17412" />
              Settings
            </span>
          </li>
          <li data-id="element-17413">
            <div className="flex items-center" data-id="element-17414">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-17415" />
              <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-17416">
                <BellIcon className="w-4 h-4 mr-2" data-id="element-17417" />
                Notifications
              </span>
            </div>
          </li>
        </ol>
      </nav>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-17418">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-17419">
          <button className={`${activeTab === 'channels' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('channels')} data-id="element-17420">
            <MessageSquareIcon className="h-4 w-4 mr-2" data-id="element-17421" />
            Channels
          </button>
          <button className={`${activeTab === 'rules' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('rules')} data-id="element-17422">
            <BookmarkIcon className="h-4 w-4 mr-2" data-id="element-17423" />
            Rules & Events
          </button>
          <button className={`${activeTab === 'preferences' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('preferences')} data-id="element-17424">
            <UserIcon className="h-4 w-4 mr-2" data-id="element-17425" />
            User Preferences
          </button>
          <button className={`${activeTab === 'overrides' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('overrides')} data-id="element-17426">
            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-17427" />
            Document Overrides
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {renderTabContent()}
    </div>;
}